/** The host bridge abstracts the CRM the softphone is embedded in, so the same
 *  connector works in Salesforce (Open CTI), in the Metafore CRM, or standalone.
 *  Three responsibilities: receive click-to-dial from the host, screen-pop the
 *  matching record on ring, and write a call-activity log when the call ends. */
import type { CallOutcome, Contact, Direction } from "../telephony/types";

export interface ScreenPopResult {
  recordId?: string;
  recordUrl?: string;
  name?: string;
  company?: string;
}

export interface CallLog {
  direction: Direction;
  number: string;
  name?: string;
  recordId?: string;
  durationSec: number;
  outcome: CallOutcome;
  startedAtMs: number;
  notes?: string;
  disposition?: string;
  /** The call's interaction id — lets the CRM fetch this call's transcript + summary
   *  (server-side) and persist it against the record. */
  interactionId?: string;
}

export interface HostHandlers {
  /** The host (a click-to-dial link / record page button) asked us to place a call. */
  onClickToDial(contact: Contact): void;
}

export interface HostBridge {
  readonly name: string;
  init(handlers: HostHandlers): Promise<void>;
  /** Resolve the CRM record for a number (screen-pop on ring). null = no match.
   *  `interactionId` (the live call's id) lets the CRM also pull this call's
   *  transcript + summary — carried on the same cti:screenPop message. */
  screenPop(number: string, direction: Direction, interactionId?: string): Promise<ScreenPopResult | null>;
  /** Persist a completed interaction to the CRM. */
  saveCallLog(log: CallLog): Promise<void>;
  /** Ask the host to show/collapse the softphone panel (best-effort). */
  setSoftphoneVisible(visible: boolean): void;
  /** Live call-state signal so the host can pop up on ring, minimise on answer, and
   *  run a call timer. Optional — hosts that don't need it can ignore it. */
  notifyCallState?(state: "ringing" | "connected" | "ended", info: { callId: string; number?: string; startedAtMs?: number }): void;
  dispose(): void;
}

/** Standalone fallback — no CRM host (opening the softphone directly). */
export class NoopHostBridge implements HostBridge {
  readonly name = "standalone";
  async init(): Promise<void> { /* nothing to wire */ }
  async screenPop(_n?: string, _d?: Direction, _i?: string): Promise<ScreenPopResult | null> { return null; }
  async saveCallLog(): Promise<void> { /* nowhere to save */ }
  setSoftphoneVisible(): void { /* no host */ }
  dispose(): void { /* noop */ }
}
